import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import axios, { type AxiosError, type AxiosResponse } from "axios";
import {
  AdminEventAuthenticityReportApi,
  type AdminEventAuthenticityReportApiAdminEventsEventIdAuthenticityReportsPostRequest,
  type AdminEventAuthenticityReportApiAdminEventsEventIdAuthenticityReportsSyncPostRequest,
  AdminEventInboundProductsApi,
  AdminEventMarketingSettingsApi,
  AdminEventPickupApi,
  type AdminEventPickupApiAdminEventsEventIdPickupsPickupIdReschedulePostRequest,
  type AdminEventPickupResponse,
  AdminEventProductSettingsApi,
  AdminEventPublicationsApi,
  type AdminEventResponse,
  AdminEventsApi,
  type AdminEventsApiAdminEventsEventIdDefaultProductImagePostRequest,
  type AdminEventsApiAdminEventsMultiplePostRequest,
  AdminEventsInboundReportApi,
  type AdminEventsInboundReportApiAdminEventsEventIdInboundReportsStoragePostRequest,
  AdminEventYieldPredictionApi,
  AdminProductsApi,
  type AdminProductSkuDto,
  type AdminUpdateMultipleProductListingsRequest,
  type BulkUpdateEventsRequest,
  type CreateEventPickupRequest,
  type CreateInboundReportRequest,
  type CreateProductForEventRequest,
  type EventDto,
  type EventPickupPrefillDataResponse,
  type EventPickupStatus,
  EventsApi,
  type EventStatus,
  type ExportEventsToNewPlatformsRequest,
  type InboundReportType,
  type PartnerRegion,
  type PublicEventDurationChangedReason,
  type SortEventOn,
  type SortEventPickupOn,
  type SortProductListingOn,
  type UpdateAndSubmitPhotoReportRequest,
  type UpdateDesignAngleRequest,
  type UpdateEventContractRequestDto,
  type UpdateEventHidePartnerRequest,
  type UpdateEventPartnerManagerRequest,
  type UpdateEventPredictedYieldRequest,
  type UpdateEventProductSettingsRequest,
  type UpdateEventPublicationsRequest,
  type UpdateEventRequestDto,
  type UpdateMarketingAngleRequest,
  type UpdateProductSkuRequestDto,
} from "mws-catalog-ts-sdk";
import { DeveloperEventsApi } from "mws-catalog-ts-sdk";
import {
  AdminFixtureDetailsApi,
  type CreateOrUpdateFixtureDetailsRequest,
} from "mws-catalog-ts-sdk-v2-admin";
import { EventsApi as PublicEventsApiV2 } from "mws-catalog-ts-sdk-v2-public";
import { SortDirection } from "mws-finance-ts-sdk";
import {
  AdminFixturesApi,
  type AdminFixturesApiAdminFixturesMultiplePostRequest,
  AdminLineupApi,
  type AdminLineupResponse,
  AdminProvidersApi,
  type CreateAthleteLineupRequest,
  type ExternalFixtureDto,
  type UpdateLineupWithAthletesRequest,
} from "mws-sports-ts-sdk";
import {
  type MaybeRef,
  type MaybeRefOrGetter,
  type Ref,
  toValue,
  unref,
} from "vue";
import { computed } from "vue";
import { useToast } from "vue-toastification";

import type { SortOrder } from "@/common/components/table/types";
import type {
  Guid,
  PaginatedResult,
  UseQueryOptionsOmitQueryKey,
} from "@/common/types";
import { getErrorMessage } from "@/common/utils/getErrorMessage";
import { usePlatformStore } from "@/platforms/store";

const catalogApiUrl = import.meta.env.VITE_CATALOG_API;
const sportsApiUrl = import.meta.env.VITE_SPORTS_API;
const adminEventsInboundReportApi = new AdminEventsInboundReportApi(
  undefined,
  catalogApiUrl,
  axios,
);
const adminEventsApi = new AdminEventsApi(undefined, catalogApiUrl, axios);
const eventsApi = new EventsApi(undefined, catalogApiUrl, axios);
const publicEventsApiV2 = new PublicEventsApiV2(undefined, catalogApiUrl);
const adminEventsPublicationsApi = new AdminEventPublicationsApi(
  undefined,
  catalogApiUrl,
);
const adminEventYieldPredictionApi = new AdminEventYieldPredictionApi(
  undefined,
  catalogApiUrl,
);
const adminEventProductSettingsApi = new AdminEventProductSettingsApi(
  undefined,
  catalogApiUrl,
);
const adminEventAuthenticityReportApi = new AdminEventAuthenticityReportApi(
  undefined,
  catalogApiUrl,
  axios,
);
const adminProductsApi = new AdminProductsApi(undefined, catalogApiUrl);
const adminSportsProvidersApi = new AdminProvidersApi(undefined, sportsApiUrl);
const adminFixturesApi = new AdminFixturesApi(undefined, sportsApiUrl);
const adminMarketingSettingsApi = new AdminEventMarketingSettingsApi(
  undefined,
  catalogApiUrl,
);
const adminEventInboundProductsApi = new AdminEventInboundProductsApi(
  undefined,
  catalogApiUrl,
);
const adminLineupApi = new AdminLineupApi(undefined, sportsApiUrl);
const developerEventsApi = new DeveloperEventsApi(undefined, catalogApiUrl);
const adminPickupsApi = new AdminEventPickupApi(undefined, catalogApiUrl);
const adminFixtureDetailsApi = new AdminFixtureDetailsApi(
  undefined,
  catalogApiUrl,
);

export enum EventMarketingSettingFilter {
  Highlighted,
  HasMarketingAngle,
  All,
}
export type EventListQueryFilters = {
  startAfter?: string;
  startBefore?: string;
  endAfter?: string;
  endBefore?: string;
  sortColumn?: string;
  sortDirection?: SortOrder;
  statuses?: EventStatus[];
  categoryId?: string;
  hasMarketingAngle?: boolean;
  hasHighlightedMarketingAngle?: boolean;
  teamTier?: number;
  managerUserId?: string;
  partnerRegion?: PartnerRegion;
  contractId?: string;
  partnerId?: string;
};
export type EventCalendarQueryFilters = {
  startAfter?: string;
  startBefore?: string;
  endsAfter?: string;
  endsBefore?: string;
  sortColumn?: string;
  sortDirection?: SortOrder;
  statuses?: EventStatus[];
  categoryId?: string;
  hasMarketingAngle?: boolean;
  hasHighlightedMarketingAngle?: boolean;
  teamTier?: number;
  managerUserId?: string;
  partnerRegion?: PartnerRegion;
  periodStartDate?: string;
};

export const getPublicEvent = async (
  eventIdentifier: string,
  platformKey: string,
) => {
  const response = await publicEventsApiV2.v2EventsIdGet({
    id: eventIdentifier,
    xPlatformKey: platformKey,
  });

  return response.data;
};

export const useEventsListQuery = (
  searchTerm: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  filters?: Ref<EventListQueryFilters>,
) => {
  const queryKey = ["events", { searchTerm, pageSize, offset, filters }];
  const { platformKey } = usePlatformStore();
  const queryFn = async () => {
    const request = {
      searchTerm: unref(searchTerm),
      pageSize: unref(pageSize),
      offset: unref(offset),
      ...unref(filters),
      managerUserId: filters?.value.managerUserId,
      startafter: filters?.value.startAfter as Date | undefined,
      startbefore: filters?.value.startBefore as Date | undefined,
      sortDirection: filters?.value.sortDirection || SortDirection.Descending,
      endafter: filters?.value.endAfter as Date | undefined,
      endbefore: filters?.value.endBefore as Date | undefined,
      statuses: filters?.value.statuses,
      partnerRegion: filters?.value.partnerRegion,
      contractId: filters?.value.contractId,
      partnerId: filters?.value.partnerId,
      xPlatformKey: unref(platformKey),
    };

    const response = await adminEventsApi.adminEventsGet(request);

    return response.data;
  };

  const options = { keepPreviousData: true };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useTeamEventsCollectionQuery =
  (teamId: MaybeRefOrGetter<string>) =>
  (_searchTerm: Ref<string>, pageSize: Ref<number>, offset: Ref<number>) => {
    const { platformKey } = usePlatformStore();

    const queryKey = ["events", "teams", { teamId, pageSize, offset }];
    const queryFn = () =>
      eventsApi.eventsTeamsTeamIdentifierGet({
        teamIdentifier: toValue(teamId),
        xPlatformKey: unref(platformKey),
        pageSize: pageSize.value,
        offSet: offset.value,
      });

    const options = { keepPreviousData: true };

    return useQuery({ queryKey, queryFn, ...options });
  };

export const useCalendaruseEventsListQuery = (
  searchTerm: Ref<string>,
  filters?: Ref<EventCalendarQueryFilters>,
) => {
  const queryKey = ["events", "calendar", { filters, searchTerm }];

  const queryFn = async () => {
    const response = await adminEventsApi.adminEventsCalendarGet({
      searchTerm: unref(searchTerm),
      startsAfter: filters?.value.startAfter as Date | undefined,
      startsBefore: filters?.value.startBefore as Date | undefined,
      endsAfter: filters?.value.endsAfter as Date | undefined,
      endsBefore: filters?.value.endsBefore as Date | undefined,
      statuses: filters?.value.statuses,
      hasHighlightedMarketingAngle: filters?.value.hasHighlightedMarketingAngle,
      hasMarketingAngle: filters?.value.hasMarketingAngle,
      managerUserId: filters?.value.managerUserId,
      teamTier: filters?.value.teamTier,
      partnerRegion: filters?.value.partnerRegion,
    });

    return response.data;
  };

  const options = { keepPreviousData: true };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useGetEventQuery = (
  eventId: MaybeRef<string>,
  platformId: MaybeRef<string>,
  options?: UseQueryOptionsOmitQueryKey<AdminEventResponse>,
) => {
  const queryKey = ["events", eventId];

  const queryFn = async () => {
    const response = await adminEventsApi.adminEventsEventIdGet({
      eventId: unref(eventId),
      xPlatformKey: unref(platformId),
    });

    return response.data;
  };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useGetEventForPlatformQuery = (
  eventId: MaybeRef<string>,
  platformId: MaybeRef<string>,
  options?: UseQueryOptionsOmitQueryKey<AdminEventResponse>,
) => {
  const queryKey = computed(() => [
    "events",
    unref(eventId),
    "platform",
    unref(platformId),
  ]);

  const queryFn = async () => {
    const response = await adminEventsApi.adminEventsEventIdGet({
      eventId: unref(eventId),
      xPlatformKey: unref(platformId),
    });

    return response.data;
  };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useGetFixtureDetailsQuery = (
  eventId: MaybeRefOrGetter<string>,
) => {
  const queryKey = ["events", eventId, "fixtureDetails"];

  const queryFn = async () => {
    const response =
      await adminFixtureDetailsApi.v2AdminFixtureDetailsEventsEventIdGet({
        eventId: toValue(eventId),
      });

    return response.data;
  };

  return useQuery({ queryKey, queryFn });
};

export const usePutFixtureDetailsMutation = (
  eventId: MaybeRefOrGetter<string>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      fixtureDetailsId: string;
      fixtureDetails: CreateOrUpdateFixtureDetailsRequest;
    }) => {
      const response =
        await adminFixtureDetailsApi.v2AdminFixtureDetailsFixtureDetailsIdPut({
          fixtureDetailsId: request.fixtureDetailsId,
          createOrUpdateFixtureDetailsRequest: request.fixtureDetails,
        });

      return response.data;
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["events", toValue(eventId), "fixtureDetails"],
      });
    },
  });
};

export const useContractEventsListQuery = (
  _: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  filters: Ref<{
    contractId: string;
    eventStatus: string | undefined;
    eventSettingsIds: string[] | undefined;
    sortOn?: SortEventOn;
    sortDirection?: SortDirection;
  }>,
) => {
  const queryKey = ["contract-events", { filters, pageSize, offset }];

  const queryFn = async () => {
    const response = await adminEventsApi.adminEventsContractContractIdGet({
      contractId: filters.value.contractId,
      eventStatus: filters.value.eventStatus,
      eventSettingsIds: filters.value.eventSettingsIds,
      offSet: offset.value,
      pageSize: pageSize.value,
      sortOn: filters.value.sortOn,
      sortDirection: filters.value.sortDirection,
    });

    return response.data;
  };

  const options = { keepPreviousData: true };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useEventYieldPredictionQuery = (
  {
    featuredTeamId,
    participatingTeamId,
    featuredTeamExternalId,
    participatingTeamExternalId,
    numberOfProducts,
  }: {
    featuredTeamId: Ref<Guid>;
    participatingTeamId: Ref<Guid>;
    featuredTeamExternalId: Ref<Guid>;
    participatingTeamExternalId: Ref<Guid>;
    numberOfProducts: Ref<number>;
  },
  options?: UseQueryOptionsOmitQueryKey<number>,
) => {
  const queryKey = [
    "eventYieldPrediction",
    {
      featuredTeamId,
      participatingTeamId,
      featuredTeamExternalId,
      participatingTeamExternalId,
      numberOfProducts,
    },
  ];

  const queryFn = async () => {
    const response =
      await adminEventYieldPredictionApi.adminEventYieldPredictionGet({
        featuredTeamId: featuredTeamId.value,
        participatingTeamId: participatingTeamId.value,
        featuredTeamExternalId: featuredTeamExternalId.value,
        participatingTeamExternalId: participatingTeamExternalId.value,
        numberOfProducts: numberOfProducts.value,
      });

    return response.data;
  };

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  });
};

export const useGetProductListingsOfEventQuery = <T = AdminProductSkuDto[]>(
  eventId: Ref<string | undefined>,
  select?: (productListings: AdminProductSkuDto[]) => T,
) => {
  const queryKey = ["events", eventId, "products"];
  const isEnabled = computed(() => Boolean(eventId.value));

  const queryFn = async () => {
    const eventIdValue = eventId.value;

    if (!eventIdValue) return Promise.reject(new Error("invalid eventId"));

    const response = await adminEventsApi.adminEventsEventIdProductsGet({
      eventId: eventIdValue,
    });

    return response.data;
  };

  return useQuery({
    queryKey,
    queryFn,
    select,
    enabled: isEnabled,
  });
};

export const useListProductListingsOfEventQuery = (
  searchTerm: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  filters: Ref<{
    eventId: string;
    sortOn?: SortProductListingOn;
    sortDirection?: SortDirection;
    isSetup?: boolean;
  }>,
) => {
  const queryKey = [
    "events",
    "products",
    "list",
    { filters, pageSize, offset },
  ];

  const queryFn = async () => {
    const response =
      await adminEventsApi.adminEventsEventIdPaginatedProductsGet({
        eventId: filters.value.eventId,
        pageSize: pageSize.value,
        offset: offset.value,
        isSetup: filters.value.isSetup,
        sortOn: filters.value.sortOn,
        sortDirection: filters.value.sortDirection,
      });

    return response.data;
  };

  return useQuery({
    queryKey,
    queryFn,
  });
};

export const useExtendEventMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      endDate: Date;
      extendReason: PublicEventDurationChangedReason;
    }) => {
      const response = await adminEventsApi.adminEventsEventIdExtendPatch({
        eventId,
        extendEventRequest: request,
      });

      return response.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events", eventId] });
    },
  });
};
export const useUpdateEventPublicationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      eventId: string;
      platformKey?: string;
      request: UpdateEventPublicationsRequest;
    }) => {
      const platformStore = usePlatformStore();
      const xPlatformKey = request.platformKey ?? platformStore.platformKey;

      const response =
        await adminEventsPublicationsApi.adminEventsEventIdPublicationsPut({
          eventId: request.eventId,
          xPlatformKey,
          updateEventPublicationsRequest: request.request,
        });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
};

export const useUpdateEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      eventId: string;
      updateEventRequest: UpdateEventRequestDto;
    }) => {
      const platformStore = usePlatformStore();

      const response = await adminEventsApi.adminEventsEventIdPatch({
        eventId: request.eventId,
        xPlatformKey: platformStore.platformKey,
        updateEventRequestDto: request.updateEventRequest,
      });

      return response.data;
    },
    onSuccess: (data: AdminEventResponse) => {
      queryClient.invalidateQueries({ queryKey: ["events", data.id] });
    },
  });
};

export const useRelistProductsOnEventMutation = () => {
  const queryClient = useQueryClient();

  const toast = useToast();
  const onErrorCallback = (error: AxiosError) => {
    const formattedMessage = error.response?.data
      ? Object.entries(error.response.data)
          .filter(([, value]) => (Array.isArray(value) ? value.length : value))
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`,
          )
          .join("\n")
      : "Something went wrong";
    toast.error(formattedMessage, { timeout: 0 }); // Let the user close it manually
  };

  return useMutation({
    mutationFn: async (request: { eventId: string; csvFile: File }) => {
      await adminEventsApi.adminEventsEventIdRelistProductsPost({
        eventId: request.eventId,
        csvFile: request.csvFile,
      });

      queryClient.invalidateQueries({
        queryKey: ["events", "products", "list"],
      });
    },
    onError: onErrorCallback,
  });
};

export const usePublishEventMutation = (
  onSuccess?: (data: EventDto) => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const onSuccessCallback = (data: EventDto) => {
    queryClient.invalidateQueries({ queryKey: ["events", data.id] });
    toast.success("Event published successfully");
    onSuccess?.(data);
  };

  const onErrorCallback = (error: AxiosError) => {
    const errorMessage = getErrorMessage(error)[0];
    toast.error(errorMessage ?? "Error publishing event");
    onError?.(error);
  };

  return useMutation({
    mutationFn: async (eventId: Guid) => {
      const response = await adminEventsApi.adminEventsEventIdPublishPatch({
        eventId,
      });

      return response.data;
    },
    onSuccess: onSuccessCallback,
    onError: onErrorCallback,
  });
};

export const useUnpublishEventMutation = (
  onSuccess?: (data: EventDto) => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const onSuccessCallback = (data: EventDto) => {
    queryClient.invalidateQueries({ queryKey: ["events", data.id] });
    toast.success("Event unpublished successfully");
    onSuccess?.(data);
  };

  const onErrorCallback = (error: AxiosError) => {
    const errorMessage = getErrorMessage(error)[0];
    toast.error(errorMessage ?? "Error unpublishing event");
    onError?.(error);
  };

  return useMutation({
    mutationFn: async (eventId: Guid) => {
      const response = await adminEventsApi.adminEventsEventIdUnpublishPatch({
        eventId,
      });

      return response.data;
    },
    onSuccess: onSuccessCallback,
    onError: onErrorCallback,
  });
};

export const useCancelEventMutation = (
  onSuccess?: (data: EventDto) => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const onSuccessCallback = (data: EventDto) => {
    queryClient.invalidateQueries({ queryKey: ["events", data.id] });
    toast.success("Event cancelled");
    onSuccess?.(data);
  };

  const onErrorCallback = (error: AxiosError) => {
    const errorMessage = getErrorMessage(error)[0];
    toast.error(errorMessage ?? "Error cancelling event");
    onError?.(error);
  };

  return useMutation({
    mutationFn: async (eventId: Guid) => {
      const response = await axios.patch<EventDto>(
        `${catalogApiUrl}/admin/events/${eventId}/cancel`,
      );

      return response.data;
    },
    onSuccess: onSuccessCallback,
    onError: onErrorCallback,
  });
};

export const useRevertCancelEventMutation = (
  onSuccess?: (data: EventDto) => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const onSuccessCallback = (data: EventDto) => {
    queryClient.invalidateQueries({ queryKey: ["events", data.id] });
    toast.success("Event is back to draft");
    onSuccess?.(data);
  };

  const onErrorCallback = (error: AxiosError) => {
    const errorMessage = getErrorMessage(error)[0];
    toast.error(errorMessage ?? "Error reverting event cancellation");
    onError?.(error);
  };

  return useMutation({
    mutationFn: async (eventId: Guid) => {
      const response = await axios.patch<EventDto>(
        `${catalogApiUrl}/admin/events/${eventId}/revert-cancellation`,
      );

      return response.data;
    },
    onSuccess: onSuccessCallback,
    onError: onErrorCallback,
  });
};

export const useGetMultipleEventsByIdsQuery = (
  ids: Ref<string[]>,
  options?: UseQueryOptionsOmitQueryKey<EventDto[]>,
) => {
  const queryKey = ["multipleEvents", { eventIds: ids }];
  const queryFn = async () => {
    const response = await adminEventsApi.adminEventsMultipleGet({
      eventIds: ids.value.join(),
    });

    return response.data;
  };

  const eventOptions = { keepPreviousData: true, ...options };

  return useQuery({ queryKey, queryFn, ...eventOptions });
};

/**
 * Mutation that updates the finance section of an event. Behind the scenes, an event's finance
 * details are actually stored on its collection(s), so this mutation will propagate the changes
 * to all of the event's collections.
 */
export const useUpdateEventContractMutation = (options: {
  onError: (err: AxiosError) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      request,
    }: {
      eventId: Ref<Guid>;
      request: UpdateEventContractRequestDto;
    }) => {
      await adminEventsApi.adminEventsEventIdContractsPatch({
        eventId: eventId.value,
        updateEventContractRequestDto: {
          partnerId: request.partnerId,
          contractId: request.contractId,
          shippingOfficeId: request.shippingOfficeId,
          regenerateCustomerInvoices: request.regenerateCustomerInvoices,
          overrideEventPropertiesFromContract:
            request.overrideEventPropertiesFromContract,
          settingsId: request.settingsId,
        },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["events", variables.eventId],
      });
    },
    onError: options.onError,
  });
};

export const useDownloadEventBidsMutation = (
  onSuccess: (data: AxiosResponse<File>) => void,
  onError: (error: AxiosError) => void,
) => {
  return useMutation({
    mutationFn: async (eventId: string) => {
      // we return the axios response here so that we can read the url
      return (await adminEventsApi.adminEventsEventIdExportBidsGet({
        eventId,
      })) as unknown as AxiosResponse<File>;
    },
    onSuccess,
    onError,
  });
};

export const useGetCategoryEventsQuery = (
  searchTerm: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  filters: Ref<{ categoryId: string; platformId: string }>,
  onError?: (error: AxiosError) => void,
  onSuccess?: (data: PaginatedResult<EventDto>) => void,
) => {
  const queryKey = [
    "events",
    filters.value.categoryId,
    { pageSize, offset, searchTerm, ...filters.value },
  ];

  const queryFn = async () => {
    const response = await adminEventsApi.adminEventsGet({
      xPlatformKey: filters.value.platformId,
      categoryId: filters.value.categoryId,
      searchTerm: searchTerm.value,
      pageSize: pageSize.value,
      offset: offset.value,
    });

    return response.data;
  };

  return useQuery({ queryKey, queryFn, ...{ onSuccess, onError } });
};

export const useUpdateEventBeneficiaryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      beneficiaryId,
      eventId,
    }: {
      beneficiaryId: string | undefined;
      eventId: string;
    }) => {
      const response = await adminEventsApi.adminEventsEventIdBeneficiaryPatch({
        eventId,
        setEventBeneficiaryRequest: {
          beneficiaryId,
        },
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
};

export const usePublishEventToPlatformMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { eventId: Guid; platformId: Guid }) => {
      const response =
        await adminEventsApi.adminEventsEventIdExportToPlatformPlatformIdPost(
          request,
        );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useDeleteBeneficiaryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      beneficiaryId,
      eventId,
    }: {
      beneficiaryId: Guid;
      eventId: string;
    }) => {
      const response = await axios.delete<void>(
        `${catalogApiUrl}/admin/events/${eventId}/beneficiary/${beneficiaryId}`,
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
};

export const useCreateEventProductListingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      eventId: Guid;
      createProductListingRequest: CreateProductForEventRequest;
    }) => {
      const response = await adminEventsApi.adminEventsEventIdProductsPost({
        eventId: request.eventId,
        createProductForEventRequest: request.createProductListingRequest,
      });

      return response.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["inboundReports"] });
      queryClient.invalidateQueries({
        queryKey: ["events", vars.eventId, "reports"],
      });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useUpdateEventProductListingSortingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      eventId: Guid;
      updateProductListingsRequest: AdminUpdateMultipleProductListingsRequest;
    }) => {
      const response = await adminProductsApi.adminProductsPatch({
        adminUpdateMultipleProductListingsRequest:
          request.updateProductListingsRequest,
      });

      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({
        queryKey: ["events", variables.eventId],
      });
    },
  });
};

export const useToggleEventProductListingMediaMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: Guid) => {
      const response = await adminEventsApi.adminEventsEventIdToggleMediaPost({
        eventId,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
};

// Gets the list of fixtures for a team within a date range
export const useGetFixturesListQuery = (
  filters: Ref<{
    sportSlug: string;
    providerSlug: string;
    teamId?: number;
    startDate?: string;
    endDate?: string;
  }>,
  options?: UseQueryOptionsOmitQueryKey<ExternalFixtureDto[]>,
) => {
  const queryKey = ["fixtures", filters];

  const queryFn = async () => {
    if (!filters.value.teamId) {
      throw new Error("Team ID is required");
    }

    const response =
      await adminSportsProvidersApi.sportSlugProvidersProviderSlugTeamsTeamIdFixturesGet(
        {
          sportSlug: filters.value.sportSlug,
          providerSlug: filters.value.providerSlug,
          teamId: filters.value.teamId,
          startDate: filters.value.startDate,
          endDate: filters.value.endDate,
        },
      );

    return response.data;
  };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useBulkUpdateEventsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      bulkUpdateEventsRequest: BulkUpdateEventsRequest;
    }) => {
      const response = await adminEventsApi.adminEventsPatch({
        bulkUpdateEventsRequest: request.bulkUpdateEventsRequest,
      });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["events"],
      });
      queryClient.invalidateQueries({
        queryKey: ["contract-events"],
      });
    },
  });
};

export const useBulkExportEventsToPlatformsMutation = (
  onSuccess: () => void,
  onError: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ExportEventsToNewPlatformsRequest) => {
      return adminEventsApi.adminEventsExportToPlatformsPost({
        exportEventsToNewPlatformsRequest: request,
      });
    },
    onSuccess: async () => {
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
    onError,
  });
};

export const useListInboundReportsQuery = (
  searchTerm: Ref,
  pageSize: Ref,
  offset: Ref,
  filters: Ref<{ eventId: MaybeRef<string> }>,
) => {
  const queryKey = ["events", filters.value.eventId, "reports", "inbound"];

  const queryFn = async () => {
    const response =
      await adminEventsInboundReportApi.adminEventsEventIdInboundReportsGet({
        eventId: unref(filters.value.eventId),
      });

    return response.data;
  };

  return useQuery({ queryKey, queryFn });
};

export const useGetEventInboundProductsQuery = (request: {
  eventId: Ref<string>;
}) => {
  const queryKey = ["events", request.eventId, "reports", "products"];

  const queryFn = async () => {
    const response =
      await adminEventInboundProductsApi.eventsEventIdInboundReportsProductsGet(
        { eventId: request.eventId.value },
      );

    return response.data;
  };

  return useQuery({ queryKey, queryFn });
};

export const useGetEventReportByIdQuery = (request: {
  eventId: Ref<string>;
  reportId: Ref<string | undefined>;
}) => {
  const queryKey = ["events", request.eventId, "reports", request.reportId];

  const isEnabled = computed(() => Boolean(request.reportId.value));

  const queryFn = async () => {
    if (!request.reportId.value)
      return Promise.reject(new Error("invalid report id for fetching report"));

    const response =
      await adminEventInboundProductsApi.eventsEventIdInboundReportsReportIdGet(
        { eventId: request.eventId.value, reportId: request.reportId.value },
      );

    return response.data;
  };

  return useQuery({ queryKey, queryFn, enabled: isEnabled });
};

export const useCreateEventReportMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (request: {
      eventId: string;
      eventProductInbounds: CreateInboundReportRequest;
    }) => {
      const response =
        await adminEventInboundProductsApi.eventsEventIdInboundReportsPost({
          eventId: request.eventId,
          createInboundReportRequest: request.eventProductInbounds,
        });

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId, "reports"],
      });
      toast.success("Report created successfully");
    },
  });
};

export const useUpdateEventHidePartnerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      request,
    }: {
      eventId: Guid;
      request: UpdateEventHidePartnerRequest;
    }) => {
      const response = await adminEventsApi.adminEventsEventIdHidePartnerPatch({
        eventId,
        updateEventHidePartnerRequest: request,
      });

      return response.data;
    },
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ["events", eventId] });
    },
  });
};

export const useUpdateEventsPartnerManagerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      request,
    }: {
      eventId: Guid;
      request: UpdateEventPartnerManagerRequest;
    }) => {
      const response =
        await adminEventsApi.adminEventsEventIdPartnerManagerPatch({
          eventId,
          updateEventPartnerManagerRequest: request,
        });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
};

export const useUpdateEventPlatformsMutation = (
  onSuccess: () => void,
  onError?: (error: AxiosError) => void,
) => {
  return useMutation({
    mutationFn: async ({
      eventId,
      platformIds,
    }: {
      eventId: Guid;
      platformIds: Guid[];
    }) => {
      return developerEventsApi.developerEventsEventIdUpdatePlatformsPatch({
        eventId,
        requestBody: platformIds,
      });
    },
    onSuccess,
    onError,
  });
};

export const useGetFixtureLineupQuery = (
  fixtureId: Ref<string>,
  teamId: Ref<string>,
  options?: UseQueryOptionsOmitQueryKey<AdminLineupResponse>,
) => {
  const queryKey = ["fixtures", fixtureId, "teams", teamId, "lineup"];

  return useQuery({
    queryKey,
    queryFn: async () => {
      const data =
        await adminLineupApi.adminLineupsTeamsTeamIdFixturesFixtureIdGet({
          teamId: teamId.value,
          fixtureId: fixtureId.value,
        });

      return data.data;
    },
    ...options,
  });
};

export const useUpdateFixtureLineupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      fixtureId: string;
      teamId: string;
      lineupId: string;
      request: UpdateLineupWithAthletesRequest;
    }) => {
      const response = await adminLineupApi.adminLineupsLineupIdAthletesPut({
        lineupId: request.lineupId,
        updateLineupWithAthletesRequest: request.request,
      });

      return response.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["fixtures", vars.fixtureId, "teams", vars.teamId, "lineup"],
      });
    },
  });
};

export const useUpdateEventsManualPredictedYieldMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      request,
    }: {
      request: UpdateEventPredictedYieldRequest;
    }) => {
      request.predictedYield = request.predictedYield / 100;
      const response =
        await adminEventYieldPredictionApi.adminEventYieldPredictionPut({
          updateEventPredictedYieldRequest: request,
        });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
};

export const useUpdateProductListingMediaFromKitMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string) => {
      const response =
        await adminEventsApi.adminEventsEventIdProductsMediaKitPatch({
          eventId,
        });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useCreateProductListingsFromLineupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string) => {
      const response =
        await adminEventsApi.adminEventsEventIdProductsCreateFromLineupPost({
          eventId,
        });

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });
};

export const useAddAthletesFromRosterMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      lineupId: string;
      fixtureId: string;
      teamId: string;
      request: CreateAthleteLineupRequest;
    }) => {
      const response = await adminLineupApi.adminLineupsLineupIdAthletesPost({
        lineupId: request.lineupId,
        createAthleteLineupRequest: request.request,
      });

      return response.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["fixtures", vars.lineupId, "lineup"],
      });
      queryClient.invalidateQueries({
        queryKey: ["fixtures", vars.fixtureId, "teams", vars.teamId, "lineup"],
      });
    },
  });
};

export const useSetEventDefaultProductImageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: AdminEventsApiAdminEventsEventIdDefaultProductImagePostRequest,
    ) => {
      const response =
        await adminEventsApi.adminEventsEventIdDefaultProductImagePost(request);

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId],
      });
    },
  });
};

export const useCreateFixturesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: AdminFixturesApiAdminFixturesMultiplePostRequest,
    ) => {
      const response =
        await adminFixturesApi.adminFixturesMultiplePost(request);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fixtures"] });
    },
  });
};

export const useCreateEventsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: AdminEventsApiAdminEventsMultiplePostRequest,
    ) => {
      const response = await adminEventsApi.adminEventsMultiplePost(request);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
};

export const useUpdateEventMarketingAngleMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = async ({
    eventId,
    request,
  }: {
    eventId: string;
    request: UpdateMarketingAngleRequest;
  }) => {
    const response =
      await adminMarketingSettingsApi.adminEventsEventIdMarketingSettingsMarketingAnglePut(
        {
          eventId,
          updateMarketingAngleRequest: request,
        },
      );

    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["events"],
      });
    },
  });
};

export const useUpdateEventDesignAngleMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = async ({
    eventId,
    request,
  }: {
    eventId: string;
    request: UpdateDesignAngleRequest;
  }) => {
    const response =
      await adminMarketingSettingsApi.adminEventsEventIdMarketingSettingsDesignAnglePut(
        {
          eventId,
          updateDesignAngleRequest: request,
        },
      );

    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["events"],
      });
    },
  });
};

export const useUpdateEventStatusMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = async ({
    eventId,
    eventStatus,
  }: {
    eventId: string;
    eventStatus: EventStatus;
  }) => {
    const response = await adminEventsApi.adminEventsEventIdStatusPut({
      eventId,
      updateEventStatusRequest: { status: eventStatus },
    });

    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId],
      });
    },
  });
};

export const useBulkUpdateEventProductListingsMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: AxiosError) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      updateProductListings: Array<UpdateProductSkuRequestDto>,
    ) => {
      const response = await adminProductsApi.adminProductsPatch({
        adminUpdateMultipleProductListingsRequest: {
          updateProducts: updateProductListings,
        },
      });

      return response.data;
    },
    onSuccess: (data) => {
      const eventId = data[0]?.eventId;
      queryClient.invalidateQueries({
        queryKey: ["events", eventId, "products"],
      });

      queryClient.invalidateQueries({ queryKey: ["products"] });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError,
  });
};

export const useUpdateEventProductListingSettingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      eventId: string;
      settings: UpdateEventProductSettingsRequest;
    }) => {
      const response =
        await adminEventProductSettingsApi.adminEventsEventIdProductSettingsPut(
          {
            eventId: request.eventId,
            updateEventProductSettingsRequest: request.settings,
          },
        );

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId],
      });
    },
  });
};

export const useCreateGetLatestInboundReportByEventIdMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      reportType,
    }: {
      eventId: string;
      reportType: string;
    }) => {
      const latestReport =
        await adminEventsInboundReportApi.adminEventsEventIdInboundReportsLatestPost(
          {
            eventId: eventId,
            reportType: reportType as InboundReportType,
          },
        );

      return latestReport.data;
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["events", vars.eventId, "reports"],
      });
      queryClient.invalidateQueries({
        queryKey: ["inboundReports", data.id],
      });
    },
  });
};

export const useGetEventInboundReportByIdQuery = (
  eventId: Ref<string>,
  reportId: Ref<string>,
) => {
  const queryKey = ["inboundReports", reportId];
  const queryFn = async () => {
    const response =
      await adminEventsInboundReportApi.adminEventsEventIdInboundReportsReportIdGet(
        {
          eventId: eventId.value,
          reportId: reportId.value,
        },
      );

    return response.data;
  };

  return useQuery({ queryKey, queryFn, refetchOnWindowFocus: false });
};

export const useCreateStorageReportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: AdminEventsInboundReportApiAdminEventsEventIdInboundReportsStoragePostRequest,
    ) => {
      const response =
        await adminEventsInboundReportApi.adminEventsEventIdInboundReportsStoragePost(
          request,
        );

      return response.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["events", vars.eventId, "reports"],
      });
    },
  });
};

export const useUpdatePhotoReportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      eventId: string;
      request: UpdateAndSubmitPhotoReportRequest;
    }) => {
      const response =
        await adminEventsInboundReportApi.adminEventsEventIdInboundReportsPhotoPost(
          {
            eventId: request.eventId,
            updateAndSubmitPhotoReportRequest: request.request,
          },
        );

      return response.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["events", vars.eventId, "reports"],
      });
    },
  });
};

export const useCreateEventPickupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      request,
    }: {
      eventId: string;
      request: CreateEventPickupRequest;
    }) => {
      const response = await adminPickupsApi.adminEventsEventIdPickupsPost({
        eventId,
        createEventPickupRequest: request,
      });

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", "pickups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId, "pickups"],
      });
    },
  });
};

export const useGetEventPickupByIdQuery = (
  eventId: Ref<string>,
  pickupId: Ref<string>,
  options?: UseQueryOptionsOmitQueryKey<AdminEventPickupResponse>,
) => {
  const queryKey = ["events", eventId, "pickups", pickupId];
  const queryFn = async () => {
    const response = await adminPickupsApi.adminEventsEventIdPickupsPickupIdGet(
      {
        eventId: eventId.value,
        pickupId: pickupId.value,
      },
    );

    return response.data;
  };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useCancelEventPickupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      pickupId,
    }: {
      eventId: string;
      pickupId: string;
    }) => {
      const response =
        await adminPickupsApi.adminEventsEventIdPickupsPickupIdCancelPost({
          pickupId: pickupId,
          eventId: eventId,
        });

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", "pickups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId, "pickups"],
      });
    },
  });
};

export const useRescheduleEventPickupMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: AdminEventPickupApiAdminEventsEventIdPickupsPickupIdReschedulePostRequest,
    ) => {
      const response =
        await adminPickupsApi.adminEventsEventIdPickupsPickupIdReschedulePost(
          request,
        );

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", "pickups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId, "pickups"],
      });
    },
  });
};
export const useGetEventPickupPrefillDataQuery = (
  eventId: Ref<string>,
  options?: UseQueryOptionsOmitQueryKey<EventPickupPrefillDataResponse>,
) => {
  const queryKey = ["events", eventId, "pickups", "prefill"];
  const queryFn = async () => {
    const response =
      await adminPickupsApi.adminEventsEventIdPickupsPrefillDataGet({
        eventId: eventId.value,
      });

    return response.data;
  };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useListPickupForEventQuery = (
  _searchTerm: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  filters: Ref<{
    eventId: string;
  }>,
) => {
  const queryKey = ["events", "pickups", { filters, pageSize, offset }];
  const queryFn = async () => {
    const response = await adminPickupsApi.adminEventsEventIdPickupsGet({
      eventId: filters.value.eventId,
      pageSize: pageSize.value,
      offset: offset.value,
    });

    return response.data;
  };

  return useQuery({ queryKey, queryFn });
};

export const useGetEventPickupsListQuery = (
  searchTerm: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  filters: Ref<{
    shippingOfficeIds?: string[];
    eventStatuses?: EventStatus[];
    eventPickupStatuses?: EventPickupStatus[];
    estimatedDeliveryDate: Date;
    sortOn?: SortEventPickupOn;
    sortDirection?: SortDirection;
  }>,
) => {
  const queryKey = ["events", "pickups", { pageSize, offset, filters }];
  const queryFn = async () => {
    const response = await adminPickupsApi.adminEventPickupsGet({
      searchTerm: searchTerm.value,
      pageSize: unref(pageSize),
      offset: unref(offset),
      shippingOfficeIds: filters?.value?.shippingOfficeIds,
      eventStatuses: filters?.value?.eventStatuses,
      eventPickupStatuses: filters?.value?.eventPickupStatuses,
      estimatedDeliveryDate: filters?.value?.estimatedDeliveryDate,
      sortDirection: filters?.value?.sortDirection,
      sortEventPickupOn: filters?.value?.sortOn,
    });

    return response.data;
  };

  return useQuery({ queryKey, queryFn });
};

export const useCreateAuthenticityReportMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (
      request: AdminEventAuthenticityReportApiAdminEventsEventIdAuthenticityReportsPostRequest,
    ) => {
      const response =
        await adminEventAuthenticityReportApi.adminEventsEventIdAuthenticityReportsPost(
          request,
        );

      return response.data;
    },
    onSuccess: (data, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId, "reports"],
      });
    },
    onError: (error: AxiosError) => {
      const errorMessage = getErrorMessage(error)[0];
      toast.error(errorMessage ?? "Error creating authenticity report");
    },
  });
};

export const useSyncAuthenticityReportMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (
      request: AdminEventAuthenticityReportApiAdminEventsEventIdAuthenticityReportsSyncPostRequest,
    ) => {
      const response =
        await adminEventAuthenticityReportApi.adminEventsEventIdAuthenticityReportsSyncPost(
          request,
        );

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId, "reports"],
      });
      toast.success("Authenticity report synced successfully");
    },
    onError: (error: AxiosError) => {
      const errorMessage = getErrorMessage(error)[0];
      toast.error(errorMessage ?? "Error syncing authenticity report");
    },
  });
};

export const useSyncInboundReportMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (request: { eventId: string; reportId: string }) => {
      await axios.post(
        `${catalogApiUrl}/admin/events/${request.eventId}/inbound-reports/${request.reportId}/sync`,
      );
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["events", request.eventId, "reports"],
      });
      toast.success("Inbound report synced to Odoo successfully");
    },
    onError: (error: AxiosError) => {
      const errorMessage = getErrorMessage(error)[0];
      toast.error(errorMessage ?? "Error syncing inbound report to Odoo");
    },
  });
};

export const useCreateEventAndProjectInOdooMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({
      eventId,
      createEventInOdoo,
      createProjectInOdoo,
    }: {
      eventId: Guid;
      createEventInOdoo?: boolean;
      createProjectInOdoo?: boolean;
    }) => {
      const response = await adminEventsApi.adminEventsEventIdOdooPost({
        eventId,
        createEventInOdoo,
        createProjectInOdoo,
      });

      return response.data;
    },
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ["events", eventId] });
      toast.success("Created in Odoo successfully");
    },
    onError: (error: AxiosError) => {
      const errorMessage = getErrorMessage(error)[0];
      toast.error(errorMessage ?? "Error creating in Odoo");
    },
  });
};

export const useCreateTaskInOdooMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (eventId: Guid) => {
      const response = await adminEventsApi.adminEventsEventIdOdooTasksPost({
        eventId,
      });

      return response.data;
    },
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({ queryKey: ["events", eventId] });
      toast.success("Task created in Odoo successfully");
    },
    onError: (error: AxiosError) => {
      const errorMessage = getErrorMessage(error)[0];
      toast.error(errorMessage ?? "Error creating task in Odoo");
    },
  });
};

export const useSyncEventAndProjectInOdooMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (eventId: Guid) => {
      const response = await adminEventsApi.adminEventsEventIdOdooPut({
        eventId,
      });

      return response.data;
    },
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({ queryKey: ["events", eventId] });
      toast.success("Synced with Odoo successfully");
    },
    onError: (error: AxiosError) => {
      const errorMessage = getErrorMessage(error)[0];
      toast.error(errorMessage ?? "Error syncing with Odoo");
    },
  });
};
